import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

interface AdminState {
  loading: boolean;
  user: User | null;
  isAdmin: boolean;
  hasAnyAdmin: boolean | null;
  refresh: () => Promise<void>;
}

export function useAdmin(): AdminState {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasAnyAdmin, setHasAnyAdmin] = useState<boolean | null>(null);

  const evaluate = async () => {
    const {
      data: { user: currentUser },
    } = await supabase.auth.getUser();
    setUser(currentUser);

    if (!currentUser) {
      setIsAdmin(false);
      setHasAnyAdmin(null);
      setLoading(false);
      return;
    }

    // Authoritative admin check via security-definer function.
    const { data: adminResult } = await supabase.rpc("is_admin");
    const admin = Boolean(adminResult);
    setIsAdmin(admin);

    // Bootstrap-funktionerna (has_any_admin/claim_first_admin) är låsta i
    // databasen sedan första administratören finns — klienten anropar dem inte.
    setHasAnyAdmin(true);
    setLoading(false);
  };

  useEffect(() => {
    evaluate();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      evaluate();
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { loading, user, isAdmin, hasAnyAdmin, refresh: evaluate };
}
